/* eslint-disable @typescript-eslint/camelcase */
/* eslint-disable react-hooks/exhaustive-deps */
import React from 'react';
import i18n from 'i18n';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  Grid,
  Button,
  IconButton,
  Tooltip,
  makeStyles,
  Checkbox,
} from '@material-ui/core';
import { Cancel as CancelIcon, Delete as DeleteIcon } from '@material-ui/icons';
import ApplicationState from 'store/interfaces';
import RefreshButton from 'components/RefreshButton';
import {
  getSessionsClientsRequest,
  removeSessionsRequest,
} from 'modules/clients/actions';
import {
  ClientSessionsFields,
  ListClientDataSessionProps,
} from 'modules/clients/interfaces';
import { GetAllUsersFields as UsersFields } from 'modules/users/interfaces';
import { modalOpen, modalClose } from 'modules/modal/actions';
import { ModalFields } from 'modules/modal/interfaces';
import TableItems, { TableCell, TableRow } from 'components/TableItems';
import { useTableFilters, Query } from 'hooks/useTableFilters';
import { formatDateWithMoment } from 'utils/helpers';
import ScreenState from 'containers/Clients/SessionClients/ScreenState';
import FormRemoveSession from 'containers/Clients/SessionClients/FormRemoveSession';
import { TableCellHeaderProps } from 'components/TableItems/interfaces';
import { selectCompany, selectPartner } from 'modules/authenticate/actions';
import { CompanyData, PartnerData } from 'modules/authenticate/interfaces';
import { getCompanyName } from 'utils/tsHelpers';
import { SearchBar } from 'components/SearchBar';
import styles from './styles';
import UserCard from './UserCard';

interface UserSessionProps {
  isLoading: boolean;
  user: UsersFields;
  userSessions: ListClientDataSessionProps;
  getSessionsClientsRequest: (props: {
    id: string;
    query?: Query & { user_uuid: string };
  }) => void;
  modalClose: () => void;
  modalOpen: (props: ModalFields) => void;
  removeSessionsRequest: ({ values, query, id }) => void;
  selectCompany: (company: CompanyData | null) => void;
  selectPartner: (partner: PartnerData | null) => void;
}
const UserSession = ({
  isLoading,
  user,
  userSessions,
  getSessionsClientsRequest,
  modalClose,
  modalOpen,
  removeSessionsRequest,
  selectCompany,
  selectPartner,
}: UserSessionProps) => {
  const classes = makeStyles(styles)();
  const {
    itemOrder,
    query,
    handleOrder,
    handleLimitChange,
    handlePageChange,
    handleSearch,
    setSearchValue,
  } = useTableFilters({
    search: '',
    limit: 10,
    offset: 0,
  });
  const [checks, setChecks] = React.useState<string[]>([]);
  const [selectedAll, setSelectedAll] = React.useState(false);
  const [refresh, setRefresh] = React.useState(false);
  const headers: Array<TableCellHeaderProps> = [
    {
      key: 'instance_name',
      i18n: i18n.t('INSTANCE_KEY'),
      order: true,
      centerTitle: false,
    },
    {
      order: true,
      key: 'create_date',
      i18n: i18n.t('CREATE_DATE_KEY'),
    },
    {
      order: true,
      key: 'status',
      i18n: i18n.t('STATUS_KEY'),
      centerTitle: true,
    },
    {
      key: 'actions',
      i18n: i18n.t('ACTIONS_KEY'),
      order: false,
      centerTitle: true,
    },
  ];

  const getUserSessions = (refresh = false) => {
    getSessionsClientsRequest({
      id: user?.client?.client_uuid,
      query: {
        user_uuid: user?.user_uuid,
        ...query,
      },
    });
    setRefresh(refresh);
  };

  React.useEffect(() => {
    getUserSessions();
  }, [getSessionsClientsRequest, query, user]);

  const handleDeleteSessions = (sessions: string[], type: string) => {
    let title = i18n.t('CLIENTS_PAGE.REMOVE_MULTIPLE_SESSIONS_TITLE_KEY');
    let subtitle = i18n.t(
      'CLIENTS_PAGE.REMOVE_MULTIPLE_SESSIONS_DESCRIPTION_KEY'
    );
    if (type !== 'multiple') {
      title = i18n.t('REMOVE_SESSION_KEY');
      subtitle = i18n.t('WANT_REMOVE_SESSION_MSG_KEY', {
        name: `${user?.first_name} ${user?.last_name}`,
      });
    }

    modalOpen({
      title,
      subtitle,
      content: (
        <FormRemoveSession
          onCancel={modalClose}
          onSubmit={values => {
            removeSessionsRequest({
              id: user?.client?.client_uuid,
              query,
              values: {
                ...values,
                sessions_ids: sessions?.map(session_id => session_id),
              },
            });
          }}
        />
      ),
      maxWidth: 'sm',
    });
  };

  const handleAllCheck = () => {
    if (!selectedAll) {
      const ids: string[] = [];
      userSessions.results.forEach(item => {
        if (!['close', 'removing'].includes(item?.status)) {
          ids.push(item?.session_id);
        }
      });

      setChecks(ids);
    } else {
      setChecks([]);
    }
    setSelectedAll(!selectedAll);
  };

  const handleCheck = (uuid: string) => {
    const findCheckId = checks?.find(item => item === uuid);
    if (!findCheckId) {
      setChecks([...checks, uuid]);
    } else {
      const index = checks?.indexOf(findCheckId);
      checks.splice(index, 1);
      setChecks([...checks]);
    }
  };

  const handleAccessInstance = () => {
    modalClose();

    if (user?.company && user?.partner) {
      selectCompany({
        id: user?.company?.company_uuid,
        name: getCompanyName(user?.company),
        shortName: getCompanyName(user?.company),
      });
      selectPartner({
        id: user?.partner?.partner_uuid,
        name: user?.partner?.partner_name,
        shortName: user?.partner?.partner_name,
      });
    }
  };

  return (
    <Grid container spacing={2}>
      <UserCard user={user} />

      <Grid
        container
        style={{ marginInline: 8, marginTop: 12 }}
        justify="space-between"
      >
        <Grid item xs={6}>
          <SearchBar
            id="search-sessions"
            isLoading={isLoading}
            handleSearch={handleSearch}
            setSearchValue={setSearchValue}
          />
        </Grid>
        <Grid item>
          <RefreshButton
            id="btn-refresh"
            animate={isLoading}
            onClick={() => getUserSessions()}
            disabled={isLoading}
          />
          <Button
            id="remove-session-bulk-client-button"
            variant="contained"
            color="inherit"
            size="small"
            className={classes.removeButton}
            startIcon={<DeleteIcon />}
            onClick={() => handleDeleteSessions(checks, 'multiple')}
            disabled={checks.length === 0}
          >
            {i18n.t('REMOVE_SESSIONS_KEY')}
          </Button>
        </Grid>
      </Grid>
      <Grid container className={classes.sessionsTable}>
        <TableItems
          header={
            <>
              <TableCell className={classes.checkboxCell}>
                <Checkbox
                  onChange={handleAllCheck}
                  id="select-all-session-clients"
                />
              </TableCell>
              {headers.map(item => (
                <TableCell
                  key={item?.key}
                  order={item?.order}
                  centerTitle={item?.centerTitle}
                  handleOrder={() =>
                    item?.order ? handleOrder(item?.key) : null
                  }
                  markOderItem={itemOrder === item?.key}
                >
                  {item?.i18n}
                </TableCell>
              ))}
            </>
          }
          renderItems={(item: ClientSessionsFields) => (
            <TableRow key={item?.session_id}>
              <TableCell className={classes.checkboxCell}>
                <Checkbox
                  disabled={['close', 'removing'].includes(item?.status)}
                  onChange={() => handleCheck(item?.session_id)}
                  checked={!!checks?.find(check => check === item?.session_id)}
                  id="select-session-clients"
                />
              </TableCell>
              <TableCell>
                {item?.instance_id ? (
                  <Tooltip title={item?.instance_id}>
                    <Link
                      className={classes.linkToInstances}
                      to={`/environments/${item?.environment_uuid}/instances?id=${item?.instance_id}`}
                      onClick={handleAccessInstance}
                    >
                      {item?.instance_name}
                    </Link>
                  </Tooltip>
                ) : (
                  <Tooltip
                    title={i18n.t('CLIENTS_PAGE.USER_NO_LINKED_INSTANCE_KEY')}
                    placement="top"
                  >
                    <CancelIcon className={classes.iconCancel} />
                  </Tooltip>
                )}
              </TableCell>
              <TableCell>{formatDateWithMoment(item?.login_at)}</TableCell>
              <TableCell align="center">
                <Tooltip
                  style={{ fontSize: 50 }}
                  title={item?.status}
                  placement="top"
                >
                  <ScreenState
                    type={item?.status}
                    gridProps={{ justify: 'center' }}
                  />
                </Tooltip>
              </TableCell>
              <TableCell align="center">
                <Tooltip title={i18n.t('REMOVE_SESSION_KEY')} placement="top">
                  <IconButton
                    disabled={['close', 'removing'].includes(item?.status)}
                    id="remove-session-client-button"
                    className={classes.buttonContainer}
                    onClick={() =>
                      handleDeleteSessions([item?.session_id], 'single')
                    }
                  >
                    <DeleteIcon className={classes.icon} />
                  </IconButton>
                </Tooltip>
              </TableCell>
            </TableRow>
          )}
          data={userSessions}
          isLoading={!refresh && isLoading}
          onChangePage={handlePageChange}
          onChangeLimit={handleLimitChange}
          notFoundText={i18n.t('CLIENTS_PAGE.NO_SESSIONS_KEY')}
          overrideRowsCount={10}
        />
      </Grid>
    </Grid>
  );
};

const mapStateToProps = ({
  clients: { isLoading, listSessionClients },
}: ApplicationState) => ({ isLoading, userSessions: listSessionClients });

export default connect(mapStateToProps, {
  getSessionsClientsRequest,
  modalClose,
  modalOpen,
  removeSessionsRequest,
  selectCompany,
  selectPartner,
})(UserSession);
